import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Pagination,
  InputAdornment,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Tooltip,
  Alert,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tab,
  Tabs,
  Collapse,
  CardActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  FileDownload as FileDownloadIcon,
  ViewColumn as ViewColumnIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { tagopsService } from '../services/tagopsService';
import { useNotifications, createErrorNotification } from './common/NotificationSystem';
import { useInventory } from '../contexts/InventoryContext';
import { extractErrorMessage } from '../utils/errorUtils';


interface InventoryItem {
  id: string;
  name: string;
  type: string;
  service: string;
  region: string;
  account: string;
  tags: Record<string, string>;
  arn: string;
  creation_time: string;
  discovery_time?: string;
  failed_to_tag?: boolean;
  name_editteble?: boolean;
  naming_convention_status?: string;
  naming_convention_acknowledged?: boolean;
  ack?: boolean;
}

interface ColumnConfig {
  key: keyof InventoryItem | 'tags' | 'naming_convention_status' | 'actions';
  label: string;
  visible: boolean;
  sortable: boolean;
  filterable: boolean;
}

const DEFAULT_NAMING_COLUMN_CONFIG: ColumnConfig[] = [
  { key: 'name', label: 'Name', visible: true, sortable: true, filterable: true },
  { key: 'id', label: 'ID', visible: true, sortable: true, filterable: true },
  { key: 'service', label: 'Service', visible: true, sortable: true, filterable: true },
  { key: 'type', label: 'Resource Type', visible: true, sortable: true, filterable: true },
  { key: 'naming_convention_status', label: 'Naming Status', visible: true, sortable: true, filterable: true },
  { key: 'actions', label: 'Actions', visible: true, sortable: false, filterable: false },
  { key: 'region', label: 'Region', visible: false, sortable: true, filterable: true },
  { key: 'account', label: 'Account', visible: false, sortable: true, filterable: true },
  { key: 'arn', label: 'ARN', visible: false, sortable: true, filterable: true },
  { key: 'tags', label: 'Tags', visible: false, sortable: false, filterable: true },
  { key: 'creation_time', label: 'Creation Time', visible: false, sortable: true, filterable: true },
  { key: 'discovery_time', label: 'Discovery Time', visible: false, sortable: true, filterable: true },
];

// Resource names are stored as the "Name" tag value; validate per AWS tag value restrictions
const NAME_AS_TAG_VALUE_RESTRICTIONS = {
  MAX_LENGTH: 256,
  ALLOWED_CHARACTERS: /^[a-zA-Z0-9\s._:/=+\-@]*$/,
};

const validateResourceName = (name: string): string | undefined => {
  const trimmed = name.trim();
  if (!trimmed) return 'Name is required';
  if (trimmed.length > NAME_AS_TAG_VALUE_RESTRICTIONS.MAX_LENGTH) {
    return `Name cannot exceed ${NAME_AS_TAG_VALUE_RESTRICTIONS.MAX_LENGTH} characters`;
  }
  if (!NAME_AS_TAG_VALUE_RESTRICTIONS.ALLOWED_CHARACTERS.test(name)) {
    return 'Name contains invalid characters. Only letters, numbers, spaces, and . : / = + - @ are allowed';
  }
  return undefined;
};

interface ServiceStats {
  service: string;
  count: number;
}

// Format service name for display (AWS style formatting)
const formatServiceName = (service: string): string => {
  if (!service) return '';
  
  const lowerService = service.toLowerCase();
  
  // AWS service name mappings
  const awsServiceNames: Record<string, string> = {
    // All caps services
    'ec2': 'EC2',
    's3': 'S3',
    'rds': 'RDS',
    'iam': 'IAM',
    'kms': 'KMS',
    'ebs': 'EBS',
    'efs': 'EFS',
    'sns': 'SNS',
    'sqs': 'SQS',
    'vpc': 'VPC',
    'ecs': 'ECS',
    'eks': 'EKS',
    'ecr': 'ECR',
    'ssm': 'SSM',
    
    // Special capitalization
    'lambda': 'Lambda',
    'batch': 'Batch',
    'config': 'Config',
    'dynamodb': 'DynamoDB',
    'cloudwatch': 'CloudWatch',
    'cloudfront': 'CloudFront',
    'cloudtrail': 'CloudTrail',
    'eventbridge': 'EventBridge',
    'codebuild': 'CodeBuild',
    'codedeploy': 'CodeDeploy',
    'redshift': 'Redshift',
    'athena': 'Athena',
    'kinesis': 'Kinesis',
    'cognito': 'Cognito',
    'cognito-idp': 'Cognito IDP',
    'cognito-identity': 'Cognito Identity',
    'sagemaker': 'SageMaker',
    'elasticache': 'ElastiCache',
    'elasticbeanstalk': 'Elastic Beanstalk',
    'elasticloadbalancing': 'Elastic Load Balancing',
    'elasticfilesystem': 'EFS',
    'route53': 'Route 53',
    'route53profiles': 'Route 53 Profiles',
    'route53resolver': 'Route 53 Resolver',
    'apigateway': 'API Gateway',
    'securityhub': 'Security Hub',
    'logs': 'CloudWatch Logs',
    'events': 'EventBridge',
    'event': 'EventBridge',
    'pipes': 'EventBridge Pipes',
    'scheduler': 'EventBridge Scheduler',
    'schemas': 'EventBridge Schemas',
    'synthetics': 'CloudWatch Synthetics',
    's3-control': 'S3 Control',
    's3control': 'S3 Control',
    'acm': 'Certificate Manager',
    'appsync': 'AppSync',
    'stepfunctions': 'Step Functions',
    'states': 'Step Functions',
    'fsx': 'FSx',
  };
  
  // Return mapped name if exists, otherwise capitalize first letter
  return awsServiceNames[lowerService] || service.charAt(0).toUpperCase() + service.slice(1).toLowerCase();
};

// Service icon mapping function
const getServiceIcon = (service: string): string => {
  const iconMap: Record<string, string> = {
    // Compute services
    'ec2': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_Amazon-EC2_48.svg',
    'lambda': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_AWS-Lambda_48.svg',
    'elasticbeanstalk': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_AWS-Elastic-Beanstalk_48.svg',
    'apprunner': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_AWS-App-Runner_48.svg',
    'app-runner': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_AWS-App-Runner_48.svg',
    'AppRunner': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_AWS-App-Runner_48.svg',
    'batch': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_AWS-Batch_48.svg',
    'EC2': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_Amazon-EC2_48.svg',
    'Lambda': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_AWS-Lambda_48.svg',
    'ElasticBeanstalk': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_AWS-Elastic-Beanstalk_48.svg',
    'Batch': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_AWS-Batch_48.svg',
    
    // Storage services
    's3': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-Simple-Storage-Service_48.svg',
    'ebs': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-Elastic-Block-Store_48.svg',
    'efs': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-EFS_48.svg',
    'fsx': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-FSx_48.svg',
    'FSx': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-FSx_48.svg',
    'backup': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_AWS-Backup_48.svg',
    'elasticfilesystem': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-EFS_48.svg',
    'ELASTICFILESYSTEM': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-EFS_48.svg',
    'S3': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-Simple-Storage-Service_48.svg',
    'EBS': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-Elastic-Block-Store_48.svg',
    'EFS': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-EFS_48.svg',
    
    // Database services
    'rds': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-RDS_48.svg',
    'dynamodb': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-DynamoDB_48.svg',
    'dax': '/icons/Resource-Icons_07312025/Res_Database/Res_Amazon-DynamoDB_Amazon-DynamoDB-Accelerator_48.svg',
    'documentdb': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-DocumentDB_48.svg',
    'docdb': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-DocumentDB_48.svg',
    'docdb-elastic': '/icons/Resource-Icons_07312025/Res_Database/Res_Amazon-DocumentDB_Elastic-Clusters_48.svg',
    'aurora': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-Aurora_48.svg',
    'aurora-dsql': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-Aurora_48.svg',
    'dsql': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-Aurora_48.svg',
    'timestream': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-Timestream_48.svg',
    'redshift': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Redshift_48.svg',
    'elasticache': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-ElastiCache_48.svg',
    'cassandra': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-Keyspaces_48.svg',
    'memorydb': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-MemoryDB_48.svg',
    
    // Networking services
    'vpc': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_Amazon-Virtual-Private-Cloud_48.svg',
    'route53': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_Amazon-Route-53_48.svg',
    'route53profiles': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_Amazon-Route-53_48.svg',
    'route53resolver': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_Amazon-Route-53_48.svg',
    'cloudfront': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_Amazon-CloudFront_48.svg',
    'apigateway': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_Amazon-API-Gateway_48.svg',
    'elasticloadbalancing': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_Elastic-Load-Balancing_48.svg',
    'directconnect': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_AWS-Direct-Connect_48.svg',
    
    // Security services
    'iam': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_AWS-Identity-and-Access-Management_48.svg',
    'kms': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_AWS-Key-Management-Service_48.svg',
    'securityhub': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_AWS-Security-Hub_48.svg',
    'secretsmanager': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_AWS-Secrets-Manager_48.svg',
    'secrets-manager': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_AWS-Secrets-Manager_48.svg',
    'acm': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_AWS-Certificate-Manager_48.svg',
    'macie': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_Amazon-Macie_48.svg',
    'macie2': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_Amazon-Macie_48.svg',
    
    // Application Integration
    'sns': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-Simple-Notification-Service_48.svg',
    'sqs': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-Simple-Queue-Service_48.svg',
    'appsync': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_AWS-AppSync_48.svg',
    'AppSync': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_AWS-AppSync_48.svg',
    'stepfunctions': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_AWS-Step-Functions_48.svg',
    'step-functions': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_AWS-Step-Functions_48.svg',
    'states': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_AWS-Step-Functions_48.svg',
    'StepFunctions': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_AWS-Step-Functions_48.svg',
    'eventbridge': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-EventBridge_48.svg',
    'events': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-EventBridge_48.svg',
    'event': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-EventBridge_48.svg',
    'eventbridge-rule': '/icons/Resource-Icons_07312025/Res_Application-Integration/Res_Amazon-EventBridge_Rule_48.svg',
    'pipes': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-EventBridge_48.svg',
    'scheduler': '/icons/Resource-Icons_07312025/Res_Application-Integration/Res_Amazon-EventBridge_Scheduler_48.svg',
    'schemas': '/icons/Resource-Icons_07312025/Res_Application-Integration/Res_Amazon-EventBridge_Schema_48.svg',
    'cognito': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_Amazon-Cognito_48.svg',
    'cognito-identity': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_Amazon-Cognito_48.svg',
    'cognito-idp': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_Amazon-Cognito_48.svg',
    'mq': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-MQ_48.svg',
    'amazonmq': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-MQ_48.svg',
    
    // Business Applications
    'ses': '/icons/Architecture-Service-Icons_07312025/Arch_Business-Applications/48/Arch_Amazon-Simple-Email-Service_48.svg',
    'amplify': '/icons/Architecture-Service-Icons_07312025/Arch_Front-End-Web-Mobile/48/Arch_AWS-Amplify_48.svg',
    
    // Management & Governance
    'cloudwatch': '/icons/Architecture-Service-Icons_07312025/Arch_Management-Governance/48/Arch_Amazon-CloudWatch_48.svg',
    'logs': '/icons/Architecture-Service-Icons_07312025/Arch_Management-Governance/48/Arch_Amazon-CloudWatch_48.svg',
    'cloudtrail': '/icons/Architecture-Service-Icons_07312025/Arch_Management-Governance/48/Arch_AWS-CloudTrail_48.svg',
    'config': '/icons/Architecture-Service-Icons_07312025/Arch_Management-Governance/48/Arch_AWS-Config_48.svg',
    'ssm': '/icons/Architecture-Service-Icons_07312025/Arch_Management-Governance/48/Arch_AWS-Systems-Manager_48.svg',
    'synthetics': '/icons/Architecture-Service-Icons_07312025/Arch_Management-Governance/48/Arch_Amazon-CloudWatch_48.svg',
    'autoscaling': '/icons/Architecture-Service-Icons_07312025/Arch_Management-Governance/48/Arch_AWS-Auto-Scaling_48.svg',
    'auto-scaling': '/icons/Architecture-Service-Icons_07312025/Arch_Management-Governance/48/Arch_AWS-Auto-Scaling_48.svg',
    
    // Containers
    'ecs': '/icons/Architecture-Service-Icons_07312025/Arch_Containers/48/Arch_Amazon-Elastic-Container-Service_48.svg',
    'eks': '/icons/Architecture-Service-Icons_07312025/Arch_Containers/48/Arch_Amazon-Elastic-Kubernetes-Service_48.svg',
    'ecr': '/icons/Architecture-Service-Icons_07312025/Arch_Containers/48/Arch_Amazon-Elastic-Container-Registry_48.svg',
    
    // Developer Tools
    'codebuild': '/icons/Architecture-Service-Icons_07312025/Arch_Developer-Tools/48/Arch_AWS-CodeBuild_48.svg',
    'codedeploy': '/icons/Architecture-Service-Icons_07312025/Arch_Developer-Tools/48/Arch_AWS-CodeDeploy_48.svg',
    'codeartifact': '/icons/Architecture-Service-Icons_07312025/Arch_Developer-Tools/48/Arch_AWS-CodeArtifact_48.svg',
    'codepipeline': '/icons/Architecture-Service-Icons_07312025/Arch_Developer-Tools/48/Arch_AWS-CodePipeline_48.svg',
    
    // Analytics
    'athena': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Athena_48.svg',
    'kinesis': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Kinesis_48.svg',
    'kinesis-data-streams': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Kinesis-Data-Streams_48.svg',
    'firehose': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Data-Firehose_48.svg',
    'kinesis-firehose': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Data-Firehose_48.svg',
    'kinesisanalytics': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Managed-Service-for-Apache-Flink_48.svg',
    'flink': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Managed-Service-for-Apache-Flink_48.svg',
    'kinesisvideo': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Kinesis-Video-Streams_48.svg',
    'emr': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-EMR_48.svg',
    'elasticmapreduce': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-EMR_48.svg',
    'emr-serverless': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-EMR_48.svg',
    'emr-containers': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-EMR_48.svg',
    'opensearch': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-OpenSearch-Service_48.svg',
    'es': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-OpenSearch-Service_48.svg',
    'opensearchserverless': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-OpenSearch-Service_48.svg',
    'aoss': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-OpenSearch-Service_48.svg',
    'msk': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Managed-Streaming-for-Apache-Kafka_48.svg',
    'kafka': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Managed-Streaming-for-Apache-Kafka_48.svg',
    'kafka-connect': '/icons/Resource-Icons_07312025/Res_Analytics/Res_Amazon-MSK_Amazon-MSK-Connect_48.svg',
    'mskconnect': '/icons/Resource-Icons_07312025/Res_Analytics/Res_Amazon-MSK_Amazon-MSK-Connect_48.svg',
    'osis': '/icons/Resource-Icons_07312025/Res_Analytics/Res_Amazon-OpenSearch-Service_OpenSearch-Ingestion_48.svg',
    'glue': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_AWS-Glue_48.svg',
    
    // AI/ML
    'bedrock': '/icons/Architecture-Service-Icons_07312025/Arch_Artificial-Intelligence/48/Arch_Amazon-Bedrock_48.svg',
    'rekognition': '/icons/Architecture-Service-Icons_07312025/Arch_Artificial-Intelligence/48/Arch_Amazon-Rekognition_48.svg',
    'sagemaker': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-SageMaker_48.svg',
    
    // S3 Control
    's3-control': '/icons/Resource-Icons_07312025/Res_Storage/Res_Amazon-Simple-Storage-Service_S3-Replication-Time-Control_48.svg',
    's3control': '/icons/Resource-Icons_07312025/Res_Storage/Res_Amazon-Simple-Storage-Service_S3-Replication-Time-Control_48.svg',
  };
  
  // Try exact match first
  if (iconMap[service]) {
    return iconMap[service];
  }
  
  // Try lowercase match
  const lowerService = service.toLowerCase();
  if (iconMap[lowerService]) {
    return iconMap[lowerService];
  }
  
  // Try to find a partial match for common patterns
  const partialMatches = Object.keys(iconMap).filter(key => 
    key.toLowerCase().includes(lowerService) || 
    lowerService.includes(key.toLowerCase())
  );
  
  if (partialMatches.length > 0) {
    return iconMap[partialMatches[0]];
  }
  
  // Default fallback
  return '/icons/Architecture-Service-Icons_07312025/Arch_General-Icons/48/Arch_AWS-Cloud_48.svg';
};

const getInventoryColumnWidth = (key: ColumnConfig['key']): number => {
  // Fixed widths (px) to keep table layout stable across filters/content changes
  switch (key) {
    case 'id':
      return 140;
    case 'name':
      return 220;
    case 'type':
      return 170;
    case 'service':
      return 200;
    case 'region':
      return 140;
    case 'account':
      return 160;
    case 'creation_time':
    case 'discovery_time':
      return 190;
    case 'tags':
      return 420;
    case 'naming_convention_status':
      return 130;
    case 'actions':
      return 150;
    case 'arn':
    default:
      return 520;
  }
};

// Manual Tag Editor Dialog Component
interface ManualTagEditorDialogProps {
  open: boolean;
  onClose: () => void;
  selectedItems: InventoryItem[];
  managedTagKeys: Set<string>;
  onSuccess: () => void;
}

interface TagInput {
  key: string;
  value: string;
  error?: string;
}

const ManualTagEditorDialog: React.FC<ManualTagEditorDialogProps> = ({
  open,
  onClose,
  selectedItems,
  managedTagKeys,
  onSuccess,
}) => {
  const { showNotification } = useNotifications();
  const [mode, setMode] = useState<'add' | 'delete'>('add');
  const [tags, setTags] = useState<TagInput[]>([{ key: '', value: '' }]);
  const [tagsToDelete, setTagsToDelete] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // AWS Tagging Restrictions (same as in RuleEditor)
  const AWS_TAGGING_RESTRICTIONS = {
    MAX_TAGS_PER_RESOURCE: 50,
    MAX_KEY_LENGTH: 128,
    MAX_VALUE_LENGTH: 256,
    ALLOWED_CHARACTERS: /^[a-zA-Z0-9\s._:/=+\-@]*$/,
    RESERVED_PREFIXES: ['aws:', 'AWS:'],
  };

  const BLACKLISTED_TAG_KEYS = [
    'aws:',
    'CreatedBy',
    'EC2ImageBuilderArn',
    'awsApplication',
    'ImageLifecycle',
    'aws-cloudformation*',
    'elasticbeanstalk:',
    'kubernetes.io/',
    'k8s.io/',
    'aws-cdk:',
    'aws-amplify:',
    'aws-copilot:'
  ];

  const isTagKeyBlacklisted = (tagKey: string): boolean => {
    return BLACKLISTED_TAG_KEYS.some(blacklistedKey => {
      if (blacklistedKey.endsWith('*')) {
        const pattern = blacklistedKey.slice(0, -1);
        return tagKey.startsWith(pattern);
      } else if (blacklistedKey.endsWith(':')) {
        return tagKey.startsWith(blacklistedKey);
      } else {
        return tagKey === blacklistedKey;
      }
    });
  };

  const validateTag = (tag: TagInput, allTags: TagInput[], currentIndex: number): string | undefined => {
    const { key, value } = tag;

    // Check for empty key (allow empty for placeholder rows)
    if (key.trim() === '') {
      return undefined; // Empty keys are allowed, will be filtered out before submission
    }

    // Check for duplicate tag keys (case-insensitive comparison)
    const trimmedKey = key.trim();
    const duplicateIndex = allTags.findIndex((t, idx) => 
      idx !== currentIndex && 
      t.key.trim().toLowerCase() === trimmedKey.toLowerCase() &&
      t.key.trim() !== ''
    );
    if (duplicateIndex !== -1) {
      return `Tag key "${trimmedKey}" is already used. Each tag key must be unique.`;
    }

    // Check key length
    if (trimmedKey.length > AWS_TAGGING_RESTRICTIONS.MAX_KEY_LENGTH) {
      return `Tag key cannot exceed ${AWS_TAGGING_RESTRICTIONS.MAX_KEY_LENGTH} characters`;
    }

    // Check value length
    if (value && value.length > AWS_TAGGING_RESTRICTIONS.MAX_VALUE_LENGTH) {
      return `Tag value cannot exceed ${AWS_TAGGING_RESTRICTIONS.MAX_VALUE_LENGTH} characters`;
    }

    // Check for reserved AWS prefixes
    const lowerTagKey = trimmedKey.toLowerCase();
    if (AWS_TAGGING_RESTRICTIONS.RESERVED_PREFIXES.some(prefix => 
      lowerTagKey.startsWith(prefix.toLowerCase())
    )) {
      return 'Tag key cannot start with "aws:" (reserved for AWS use)';
    }

    // Check for blacklisted keys
    if (isTagKeyBlacklisted(trimmedKey)) {
      return `Tag key "${trimmedKey}" is reserved for AWS internal use`;
    }

    // Check for invalid characters in key
    if (!AWS_TAGGING_RESTRICTIONS.ALLOWED_CHARACTERS.test(trimmedKey)) {
      return 'Tag key contains invalid characters. Only letters, numbers, spaces, and . : / = + - @ are allowed';
    }

    // Check for invalid characters in value
    if (value && !AWS_TAGGING_RESTRICTIONS.ALLOWED_CHARACTERS.test(value)) {
      return 'Tag value contains invalid characters. Only letters, numbers, spaces, and . : / = + - @ are allowed';
    }

    // Check if tag key is managed by rules
    if (managedTagKeys.has(trimmedKey)) {
      return `Tag key "${trimmedKey}" is managed by Tagging Rules and cannot be manually edited`;
    }

    return undefined;
  };

  const handleTagChange = (index: number, field: 'key' | 'value', value: string) => {
    setTags(prev => {
      const newTags = [...prev];
      newTags[index] = { ...newTags[index], [field]: value, error: undefined };
      
      // Re-validate all tags when a key changes (to check for duplicates)
      if (field === 'key') {
        // Validate all tags to check for duplicates
        newTags.forEach((tag, idx) => {
          if (tag.key.trim()) {
            const error = validateTag(tag, newTags, idx);
            // Update error state (set error if exists, clear if no error)
            newTags[idx] = { ...tag, error: error || undefined };
          } else {
            // Clear error for empty tags
            newTags[idx] = { ...tag, error: undefined };
          }
        });
      }
      
      return newTags;
    });
  };

  const handleAddTag = () => {
    setTags(prev => [...prev, { key: '', value: '' }]);
  };

  const handleRemoveTag = (index: number) => {
    setTags(prev => {
      const newTags = prev.filter((_, i) => i !== index);
      // Re-validate all remaining tags after removal (duplicates may be resolved)
      newTags.forEach((tag, idx) => {
        if (tag.key.trim()) {
          const error = validateTag(tag, newTags, idx);
          if (error) {
            newTags[idx] = { ...tag, error };
          } else {
            newTags[idx] = { ...tag, error: undefined };
          }
        } else {
          newTags[idx] = { ...tag, error: undefined };
        }
      });
      return newTags;
    });
  };

  const handleSubmit = async () => {
    if (mode === 'add') {
      // Validate all tags (including duplicate check)
      const errors: string[] = [];
      const validatedTags: TagInput[] = tags.map((tag, index) => {
        const error = validateTag(tag, tags, index);
        if (error) {
          errors.push(error);
          return { ...tag, error };
        }
        return tag;
      });

      if (errors.length > 0) {
        setTags(validatedTags);
        showNotification({
          type: 'error',
          title: 'Validation Error',
          message: 'Please fix validation errors before submitting. Make sure tag keys are unique.',
        });
        return;
      }

      // Filter out empty tags
      const validTags = tags
        .filter(tag => tag.key.trim() !== '')
        .map(tag => ({ [tag.key.trim()]: tag.value.trim() }));

      if (validTags.length === 0) {
        showNotification({
          type: 'warning',
          title: 'No Tags',
          message: 'Please add at least one tag',
        });
        return;
      }

      // Double-check for duplicate keys (shouldn't happen, but safety check)
      const tagKeys = validTags.map(tag => Object.keys(tag)[0].toLowerCase());
      const uniqueKeys = new Set(tagKeys);
      if (tagKeys.length !== uniqueKeys.size) {
        showNotification({
          type: 'error',
          title: 'Duplicate Tag Keys',
          message: 'Duplicate tag keys detected. Please ensure each tag key is unique.',
        });
        return;
      }

      // Check total tag count per resource
      const maxTagsPerResource = AWS_TAGGING_RESTRICTIONS.MAX_TAGS_PER_RESOURCE;
      for (const item of selectedItems) {
        const existingTagCount = Object.keys(item.tags || {}).length;
        const newTagCount = validTags.length;
        const duplicateKeys = validTags.filter(tag => 
          Object.keys(item.tags || {}).includes(Object.keys(tag)[0])
        );
        const uniqueNewTags = newTagCount - duplicateKeys.length;
        
        if (existingTagCount + uniqueNewTags > maxTagsPerResource) {
          showNotification({
            type: 'error',
            title: 'Tag Limit Exceeded',
            message: `Resource ${item.name || item.arn} would exceed the maximum of ${maxTagsPerResource} tags per resource`,
          });
          return;
        }
      }

      setIsSubmitting(true);
      try {
        // Prepare resource ARNs and tags
        const resourceArns = selectedItems.map(item => item.arn);
        const tagsDict: Record<string, string> = {};
        validTags.forEach(tag => {
          Object.assign(tagsDict, tag);
        });
        
        // Prepare resource regions mapping (ARN -> region)
        const resourceRegions: Record<string, string> = {};
        selectedItems.forEach(item => {
          if (item.region) {
            resourceRegions[item.arn] = item.region;
          }
        });
        
        // Call backend API to apply tags
        const response = await tagopsService.addTagsManually(resourceArns, tagsDict, resourceRegions);
        
        if (response.failed > 0) {
          const failedArns = response.results
            .filter(r => r.status === 'failed')
            .map(r => r.arn)
            .slice(0, 3)
            .join(', ');
          showNotification({
            type: 'warning',
            title: 'Partial Success',
            message: `${response.successful} of ${response.total_resources} resources tagged successfully. ${response.failed} failed.${failedArns ? ` Failed: ${failedArns}${response.failed > 3 ? '...' : ''}` : ''}`,
          });
        } else {
          showNotification({
            type: 'success',
            title: 'Tags Applied Successfully',
            message: `${response.successful} tag(s) applied to ${response.total_resources} resource(s) successfully.`,
          });
        }
        
        // Reset form
        setTags([{ key: '', value: '' }]);
        onSuccess();
      } catch (error: any) {
        const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to apply tags';
        showNotification({
          type: 'error',
          title: 'Failed to Apply Tags',
          message: errorMessage,
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Delete mode
      if (tagsToDelete.length === 0) {
        showNotification({
          type: 'warning',
          title: 'No Tags Selected',
          message: 'Please select at least one tag to delete',
        });
        return;
      }

      setIsSubmitting(true);
      try {
        // Prepare resource ARNs
        const resourceArns = selectedItems.map(item => item.arn);
        
        // Prepare resource regions mapping (ARN -> region)
        const resourceRegions: Record<string, string> = {};
        selectedItems.forEach(item => {
          if (item.region) {
            resourceRegions[item.arn] = item.region;
          }
        });
        
        // Call backend API to delete tags
        const response = await tagopsService.deleteTagsManually(resourceArns, tagsToDelete, resourceRegions);
        
        if (response.failed > 0) {
          const failedArns = response.results
            .filter(r => r.status === 'failed')
            .map(r => r.arn)
            .slice(0, 3)
            .join(', ');
          showNotification({
            type: 'warning',
            title: 'Partial Success',
            message: `${response.successful} of ${response.total_resources} resources updated successfully. ${response.failed} failed.${failedArns ? ` Failed: ${failedArns}${response.failed > 3 ? '...' : ''}` : ''}`,
          });
        } else {
          showNotification({
            type: 'success',
            title: 'Tags Deleted Successfully',
            message: `${tagsToDelete.length} tag key(s) deleted from ${response.total_resources} resource(s) successfully.`,
          });
        }
        
        // Reset form
        setTagsToDelete([]);
        onSuccess();
      } catch (error: any) {
        const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to delete tags';
        showNotification({
          type: 'error',
          title: 'Failed to Delete Tags',
          message: errorMessage,
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Get all existing tags from selected items
  const existingTags = useMemo(() => {
    const tagSet = new Set<string>();
    selectedItems.forEach(item => {
      if (item.tags) {
        Object.keys(item.tags).forEach(key => {
          // Only include tags that are not managed by rules
          if (!managedTagKeys.has(key)) {
            tagSet.add(key);
          }
        });
      }
    });
    return Array.from(tagSet).sort();
  }, [selectedItems, managedTagKeys]);

  const handleClose = () => {
    setTags([{ key: '', value: '' }]);
    setTagsToDelete([]);
    setMode('add');
    onClose();
  };

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: 'add' | 'delete' | null) => {
    if (newMode !== null) {
      setMode(newMode);
      // Reset form when switching modes
      if (newMode === 'add') {
        setTagsToDelete([]);
      } else {
        setTags([{ key: '', value: '' }]);
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Modify Tags Manually</Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedItems.length} resource(s) selected
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={handleModeChange}
              aria-label="tag operation mode"
            >
              <ToggleButton value="add" aria-label="add tags">
                Add Tags
              </ToggleButton>
              <ToggleButton value="delete" aria-label="delete tags">
                Delete Tags
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {mode === 'add' 
                ? 'Add tags to the selected resources. Tag keys managed by Tagging Rules cannot be manually edited.'
                : 'Delete tags from the selected resources. Only tags that are not managed by Tagging Rules can be deleted.'}
            </Typography>
          </Alert>
          
          {managedTagKeys.size > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Managed Tag Keys (cannot be edited manually):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Array.from(managedTagKeys).map(key => (
                  <Chip key={key} label={key} size="small" variant="outlined" />
                ))}
              </Box>
            </Alert>
          )}
        </Box>

        {mode === 'add' ? (
          <>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Tags to Add:
            </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {tags.map((tag, index) => {
            // Use stored error or validate tag on render to show errors
            const error = tag.error || (tag.key.trim() ? validateTag(tag, tags, index) : undefined);
            const hasError = !!error;
            
            return (
              <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  label="Tag Key"
                  value={tag.key}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    handleTagChange(index, 'key', newValue);
                  }}
                  onBlur={() => {
                    // Validate on blur (this will check for duplicates)
                    if (tag.key.trim()) {
                      setTags(prev => {
                        const newTags = [...prev];
                        const validationError = validateTag(newTags[index], newTags, index);
                        if (validationError) {
                          newTags[index] = { ...newTags[index], error: validationError };
                        } else {
                          newTags[index] = { ...newTags[index], error: undefined };
                        }
                        return newTags;
                      });
                    }
                  }}
                  error={hasError}
                  helperText={error}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Tag Value"
                  value={tag.value}
                  onChange={(e) => handleTagChange(index, 'value', e.target.value)}
                  onBlur={() => {
                    // Validate on blur (check for duplicates and other validation)
                    if (tag.key.trim()) {
                      setTags(prev => {
                        const newTags = [...prev];
                        const validationError = validateTag(newTags[index], newTags, index);
                        if (validationError) {
                          newTags[index] = { ...newTags[index], error: validationError };
                        } else {
                          newTags[index] = { ...newTags[index], error: undefined };
                        }
                        return newTags;
                      });
                    }
                  }}
                  error={hasError && error.includes('value')}
                  helperText={hasError && error.includes('value') ? error : undefined}
                  fullWidth
                  size="small"
                />
                <IconButton
                  onClick={() => handleRemoveTag(index)}
                  disabled={tags.length === 1}
                  color="error"
                  sx={{ mt: 0.5 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            );
          })}
        </Box>

            <Button
              startIcon={<AddIcon />}
              onClick={handleAddTag}
              sx={{ mt: 2 }}
              size="small"
            >
              Add Another Tag
            </Button>

            {tags.some(tag => tag.error) && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Please fix the validation errors above before submitting.
                </Typography>
              </Alert>
            )}
          </>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Select Tags to Delete:
            </Typography>
            
            {existingTags.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  No deletable tags found on the selected resources. All existing tags are managed by Tagging Rules.
                </Typography>
              </Alert>
            ) : (
              <Autocomplete
                multiple
                options={existingTags}
                value={tagsToDelete}
                onChange={(_, newValue) => setTagsToDelete(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tag Keys to Delete"
                    placeholder="Select tag keys..."
                    helperText={`${tagsToDelete.length} tag key(s) selected`}
                  />
                )}
                renderTags={(value, getTagProps) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {value.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        key={option}
                        color="error"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Typography variant="body2">{option}</Typography>
                  </Box>
                )}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            isSubmitting || 
            (mode === 'add' && (tags.length === 0 || tags.every(t => !t.key.trim()) || tags.some(t => t.error))) ||
            (mode === 'delete' && tagsToDelete.length === 0)
          }
          color={mode === 'delete' ? 'error' : 'primary'}
        >
          {isSubmitting 
            ? (mode === 'add' ? 'Applying...' : 'Deleting...') 
            : (mode === 'add' ? 'Apply Tags' : 'Delete Tags')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Separate search input component to prevent parent re-renders on keystroke
const SearchInput: React.FC<{
  value: string;
  onSearch: (value: string) => void;
  placeholder?: string;
}> = React.memo(({ value, onSearch, placeholder = "Search resources... (Press Enter to search)" }) => {
  const [localValue, setLocalValue] = useState(value);
  
  // Sync with external value when it changes (e.g., on clear filters)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSearch(localValue);
    }
  }, [localValue, onSearch]);
  
  return (
    <TextField
      fullWidth
      placeholder={placeholder}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onKeyDown={handleKeyDown}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
      }}
    />
  );
});

export const Inventory: React.FC = () => {
  const { showNotification } = useNotifications();
  const { inventoryData, loading, error, refreshInventory } = useInventory();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [serviceStats, setServiceStats] = useState<ServiceStats[]>([]);
  const [availableServices, setAvailableServices] = useState<string[]>([]);
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  const [availableResourceTypes, setAvailableResourceTypes] = useState<string[]>([]);
  const [availableAccounts, setAvailableAccounts] = useState<Array<{id: string, name: string}>>([]);
  const [availableTags, setAvailableTags] = useState<Array<{key: string, value: string, display: string}>>([]);
  const [sortField, setSortField] = useState<keyof InventoryItem | 'tags' | 'naming_convention_status' | 'actions'>('arn');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set()); // Store ARNs of selected items
  const [manualTagDialogOpen, setManualTagDialogOpen] = useState(false);
  const [managedTagKeys, setManagedTagKeys] = useState<Set<string>>(new Set());
  
  // Filter states - separate for each view mode
  // Tagging view filters
  const [taggingFilters, setTaggingFilters] = useState({
    searchTerm: '',
    selectedService: 'all',
    selectedRegion: 'all',
    selectedResourceType: 'all',
    selectedAccount: 'all',
    selectedFailedToTag: 'all',
    selectedUntagged: 'all',
    selectedTags: new Set<string>(),
    excludedTags: new Set<string>(),
  });
  
  // Naming convention view filters
  const [namingFilters, setNamingFilters] = useState({
    searchTerm: '',
    selectedService: 'all',
    selectedRegion: 'all',
    selectedResourceType: 'all',
    selectedAccount: 'all',
    selectedNamingStatus: 'all', // 'all', 'valid', 'invalid'
  });
  
  // Filter expansion state
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // View Mode state (Tag Management vs Naming Compliance)
  const [searchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<'tagging' | 'naming'>(() =>
    searchParams.get('view') === 'naming' ? 'naming' : 'tagging'
  );
  const [acknowledgmentTab, setAcknowledgmentTab] = useState<0 | 1>(0); // 0 = Not Acknowledged, 1 = Acknowledged
  const [editNameDialogOpen, setEditNameDialogOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<InventoryItem | null>(null);
  const [editingName, setEditingName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [acknowledgedArns, setAcknowledgedArns] = useState<Set<string>>(new Set());
  
  // Column configuration state
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>([
    { key: 'arn', label: 'ARN', visible: true, sortable: true, filterable: true },
    { key: 'name', label: 'Name', visible: true, sortable: true, filterable: true },
    { key: 'type', label: 'Type', visible: true, sortable: true, filterable: true },
    { key: 'service', label: 'Service', visible: true, sortable: true, filterable: true },
    { key: 'region', label: 'Region', visible: true, sortable: true, filterable: true },
    { key: 'tags', label: 'Tags', visible: true, sortable: false, filterable: true },
    { key: 'account', label: 'Account', visible: false, sortable: true, filterable: true },
    { key: 'id', label: 'ID', visible: false, sortable: true, filterable: true },
    { key: 'creation_time', label: 'Creation Time', visible: false, sortable: true, filterable: true },
    { key: 'discovery_time', label: 'Discovery Time', visible: false, sortable: true, filterable: true },
  ]);
  const [namingColumnConfig, setNamingColumnConfig] = useState<ColumnConfig[]>(() =>
    DEFAULT_NAMING_COLUMN_CONFIG.map((c) => ({ ...c }))
  );
  
  // Column customization dialog state
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);

  // Fetch managed tag keys from rules
  useEffect(() => {
    const fetchManagedTagKeys = async () => {
      try {
        const ruleConfig = await tagopsService.getRuleConfig();
        const managedKeys = new Set<string>();
        
        // Extract tag keys from rules
        if (ruleConfig.rules?.rules) {
          Object.values(ruleConfig.rules.rules).forEach((ruleGroup: any) => {
            if (typeof ruleGroup === 'object' && ruleGroup !== null) {
              Object.values(ruleGroup).forEach((rule: any) => {
                if (rule?.rule) {
                  // Handle 'add' type rules with tags
                  if (rule.rule.type === 'add' && rule.rule.tags) {
                    if (Array.isArray(rule.rule.tags)) {
                      rule.rule.tags.forEach((tag: any) => {
                        if (typeof tag === 'object' && tag !== null) {
                          Object.keys(tag).forEach(key => managedKeys.add(key));
                        }
                      });
                    }
                  }
                  // Handle 'template' type rules
                  if (rule.rule.type === 'template' && rule.rule.templateName) {
                    const template = ruleConfig.rules?.templates?.find(
                      (t: any) => {
                        if (typeof t === 'object' && t !== null) {
                          return t.name === rule.rule.templateName || Object.keys(t)[0] === rule.rule.templateName;
                        }
                        return false;
                      }
                    );
                    if (template) {
                      const templateData = (template as any)[rule.rule.templateName] || template;
                      // Extract from constantTags
                      if (templateData.constantTags && Array.isArray(templateData.constantTags)) {
                        templateData.constantTags.forEach((tag: any) => {
                          if (typeof tag === 'object' && tag !== null) {
                            Object.keys(tag).forEach(key => managedKeys.add(key));
                          }
                        });
                      }
                      // Extract from generalTags (these are tag keys)
                      if (templateData.generalTags && Array.isArray(templateData.generalTags)) {
                        templateData.generalTags.forEach((tagKey: string) => {
                          if (typeof tagKey === 'string') {
                            managedKeys.add(tagKey);
                          }
                        });
                      }
                    }
                  }
                }
              });
            }
          });
        }
        
        // Also check templates directly
        if (ruleConfig.rules?.templates && Array.isArray(ruleConfig.rules.templates)) {
          ruleConfig.rules.templates.forEach((templateObj: any) => {
            Object.values(templateObj).forEach((template: any) => {
              if (template.constantTags && Array.isArray(template.constantTags)) {
                template.constantTags.forEach((tag: any) => {
                  if (typeof tag === 'object' && tag !== null) {
                    Object.keys(tag).forEach(key => managedKeys.add(key));
                  }
                });
              }
              if (template.generalTags && Array.isArray(template.generalTags)) {
                template.generalTags.forEach((tagKey: string) => {
                  if (typeof tagKey === 'string') {
                    managedKeys.add(tagKey);
                  }
                });
              }
            });
          });
        }
        
        setManagedTagKeys(managedKeys);
      } catch (error) {
        console.error('Failed to fetch managed tag keys:', error);
        // Continue without validation if fetch fails
      }
    };
    
    fetchManagedTagKeys();
  }, []);
  



  // Update inventory data when context data changes
  useEffect(() => {
    if (inventoryData) {
      // Map ack field to naming_convention_acknowledged for backward compatibility
      const mappedResources = inventoryData.resources.map((item: any) => ({
        ...item,
        naming_convention_acknowledged: item.ack === true || item.naming_convention_acknowledged === true
      }));
      setInventory(mappedResources);
      
      // Update service stats from API response
      const stats: ServiceStats[] = Object.entries(inventoryData.service_stats).map(([service, count]) => ({
        service,
        count: count as number
      }));
      setServiceStats(stats);
      
      // Update available services for dropdown
      const services = Object.keys(inventoryData.service_stats);
      setAvailableServices(services);
      
      // Extract unique regions from inventory data
      const regions = Array.from(new Set(inventoryData.resources.map(item => item.region)))
        .filter(region => region && region.trim() !== '')
        .sort();
      setAvailableRegions(regions);
      
      // Reset region filter if it's no longer available (for both filter sets)
      if (taggingFilters.selectedRegion !== 'all' && !regions.includes(taggingFilters.selectedRegion)) {
        setTaggingFilters(prev => ({ ...prev, selectedRegion: 'all' }));
      }
      if (namingFilters.selectedRegion !== 'all' && !regions.includes(namingFilters.selectedRegion)) {
        setNamingFilters(prev => ({ ...prev, selectedRegion: 'all' }));
      }
      
      // Extract unique resource types from inventory data
      // If a service is selected, only show resource types for that service
      // Use the current view mode's selected service
      const currentService = viewMode === 'tagging' ? taggingFilters.selectedService : namingFilters.selectedService;
      let resourceTypes: string[];
      if (currentService !== 'all') {
        resourceTypes = Array.from(
          new Set(
            inventoryData.resources
              .filter(item => item.service.toLowerCase() === currentService.toLowerCase())
              .map(item => item.type)
          )
        ).sort();
      } else {
        resourceTypes = Array.from(new Set(inventoryData.resources.map(item => item.type))).sort();
      }
      setAvailableResourceTypes(resourceTypes);
      
      // Reset resource type filter if it's no longer available for the selected service
      if (taggingFilters.selectedResourceType !== 'all' && !resourceTypes.includes(taggingFilters.selectedResourceType)) {
        setTaggingFilters(prev => ({ ...prev, selectedResourceType: 'all' }));
      }
      if (namingFilters.selectedResourceType !== 'all' && !resourceTypes.includes(namingFilters.selectedResourceType)) {
        setNamingFilters(prev => ({ ...prev, selectedResourceType: 'all' }));
      }
      
      // Extract unique accounts from inventory data
      // Merge with existing availableAccounts to preserve account names from API
      setAvailableAccounts(prevAccounts => {
        const accountIdsFromInventory = Array.from(new Set(inventoryData.resources.map(item => item.account)))
          .filter(account => account && account.trim() !== '');
        
        // Create a map of existing accounts to preserve names
        const existingAccountMap = new Map(prevAccounts.map(acc => [acc.id, acc]));
        
        // Merge: use existing account data if available, otherwise create new entry
        const mergedAccounts = accountIdsFromInventory.map(accountId => {
          const existing = existingAccountMap.get(accountId);
          return existing || { id: accountId, name: accountId };
        }).sort((a, b) => a.id.localeCompare(b.id));
        
        return mergedAccounts;
      });
      
      // Extract unique tags from inventory data
      const tagSet = new Set<string>();
      inventoryData.resources.forEach(item => {
        if (item.tags) {
          Object.entries(item.tags).forEach(([key, value]) => {
            if (key && value) {
              tagSet.add(`${key}=${value}`);
            }
          });
        }
      });
      
      const uniqueTags = Array.from(tagSet)
        .map(tagStr => {
          const [key, ...valueParts] = tagStr.split('=');
          const value = valueParts.join('='); // Handle values that contain '='
          return {
            key,
            value,
            display: `${key}: ${value}`
          };
        })
        .sort((a, b) => {
          // Sort by key first, then by value
          const keyCompare = a.key.localeCompare(b.key);
          if (keyCompare !== 0) return keyCompare;
          return a.value.localeCompare(b.value);
        });
      
      setAvailableTags(uniqueTags);
      
      // Reset filters if the selected service is no longer available
      if (taggingFilters.selectedService !== 'all' && !services.includes(taggingFilters.selectedService)) {
        setTaggingFilters(prev => ({ ...prev, selectedService: 'all' }));
      }
      if (namingFilters.selectedService !== 'all' && !services.includes(namingFilters.selectedService)) {
        setNamingFilters(prev => ({ ...prev, selectedService: 'all' }));
      }
      
    }
  }, [inventoryData, taggingFilters.selectedService, taggingFilters.selectedResourceType, taggingFilters.selectedRegion, namingFilters.selectedService, namingFilters.selectedResourceType, namingFilters.selectedRegion, viewMode]);

  // Handle errors from context
  useEffect(() => {
    if (error) {
      showNotification(createErrorNotification(new Error(error), refreshInventory));
    }
  }, [error, showNotification, refreshInventory]);

  // Fetch account data from API
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const accountsData = await tagopsService.getAccounts();
        if (accountsData && accountsData.accounts && Array.isArray(accountsData.accounts)) {
          // Transform API response to match our format
          const formattedAccounts = accountsData.accounts.map((acc: any) => ({
            id: acc.accountId || acc.id,
            name: acc.account_name || acc.accountName || acc.accountId || acc.id || 'Unknown'
          }));
          setAvailableAccounts(formattedAccounts);
        } else if (accountsData && Array.isArray(accountsData)) {
          // Handle case where API returns array directly
          const formattedAccounts = accountsData.map((acc: any) => ({
            id: acc.accountId || acc.id,
            name: acc.account_name || acc.accountName || acc.accountId || acc.id || 'Unknown'
          }));
          setAvailableAccounts(formattedAccounts);
        }
      } catch (err) {
        // If API fails, we'll still have accounts from inventory data
        console.warn('Failed to fetch accounts from API, using inventory data only:', err);
      }
    };

    fetchAccounts();
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [taggingFilters, namingFilters, viewMode, sortField, sortDirection, acknowledgmentTab]);


  const sortInventory = (items: InventoryItem[]) => {
    return [...items].sort((a, b) => {
      // Handle naming_convention_status sorting
      if (sortField === 'naming_convention_status') {
        const aStatus = a.naming_convention_status || '';
        const bStatus = b.naming_convention_status || '';
        // Sort: valid < invalid < not_defined/empty
        const aOrder = aStatus === 'valid' ? 0 : aStatus === 'invalid' ? 1 : (aStatus === 'not_defined' || !aStatus ? 2 : 3);
        const bOrder = bStatus === 'valid' ? 0 : bStatus === 'invalid' ? 1 : (bStatus === 'not_defined' || !bStatus ? 2 : 3);
        if (sortDirection === 'asc') {
          return aOrder - bOrder;
        } else {
          return bOrder - aOrder;
        }
      }
      
      // Actions column is not sortable
      if (sortField === 'actions') {
        return 0;
      }
      
      let aValue = a[sortField as keyof InventoryItem];
      let bValue = b[sortField as keyof InventoryItem];

      // Handle epoch sorting for creation_time / discovery_time (unknown sorts last in asc)
      if (sortField === 'creation_time' || sortField === 'discovery_time') {
        const epochSortKey = (raw: string | undefined): number => {
          const v = raw == null ? '' : String(raw).trim();
          if (!v || v.toLowerCase() === 'unknown') return Number.POSITIVE_INFINITY;
          if (/^\d+$/.test(v)) return parseInt(v, 10) * 1000;
          const ms = new Date(v).getTime();
          return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
        };
        const aDate = epochSortKey(a[sortField]);
        const bDate = epochSortKey(b[sortField]);
        return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }

      // Handle string sorting
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      
      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  };

  // Check if resource is acknowledged (either from API or local state)
  const isResourceAcknowledged = (item: InventoryItem): boolean => {
    // Check ack field from API (primary source)
    if (item.ack === true) {
      return true;
    }
    // Fallback to naming_convention_acknowledged or local state
    return item.naming_convention_acknowledged === true || acknowledgedArns.has(item.arn);
  };

  // Check if naming convention is invalid
  const isNamingConventionInvalid = (item: InventoryItem): boolean => {
    return item.naming_convention_status === 'invalid';
  };

  // Check if naming convention is valid
  const isNamingConventionValid = (item: InventoryItem): boolean => {
    return item.naming_convention_status === 'valid';
  };

  // Memoize filtered inventory to prevent recalculation on every render
  // Only recalculates when actual filter state changes, not when local input state changes
  const filteredInventory = useMemo(() => {
    return inventory.filter(item => {
      // Use the appropriate filter set based on view mode
      const filters = viewMode === 'tagging' ? taggingFilters : namingFilters;
      
      // Enhanced search including tag values
      const searchLower = filters.searchTerm.toLowerCase();
      const matchesSearch = (item.name || '').toLowerCase().includes(searchLower) ||
                           (item.id || '').toLowerCase().includes(searchLower) ||
                           (item.type || '').toLowerCase().includes(searchLower) ||
                           (item.arn || '').toLowerCase().includes(searchLower) ||
                           Object.values(item.tags || {}).some(tagValue => 
                             tagValue.toLowerCase().includes(searchLower)
                           ) ||
                           Object.keys(item.tags || {}).some(tagKey => 
                             tagKey.toLowerCase().includes(searchLower)
                           );
      
      const matchesService = filters.selectedService === 'all' || item.service.toLowerCase() === filters.selectedService.toLowerCase();
      const matchesRegion = filters.selectedRegion === 'all' || item.region === filters.selectedRegion;
      const matchesResourceType = filters.selectedResourceType === 'all' || item.type === filters.selectedResourceType;
      const matchesAccount = filters.selectedAccount === 'all' || item.account === filters.selectedAccount;
      
      // Tagging view specific filters
      if (viewMode === 'tagging') {
        const taggingFiltersTyped = filters as typeof taggingFilters;
        
        // Handle failed_to_tag filter (default to false if not present)
        const failedToTag = item.failed_to_tag ?? false;
        const matchesFailedToTag = taggingFiltersTyped.selectedFailedToTag === 'all' || 
                                    (taggingFiltersTyped.selectedFailedToTag === 'true' && failedToTag === true) ||
                                    (taggingFiltersTyped.selectedFailedToTag === 'false' && failedToTag === false);
        
        // Handle untagged filter - check if resource has no tags
        const hasTags = item.tags && Object.keys(item.tags).length > 0;
        const matchesUntagged = taggingFiltersTyped.selectedUntagged === 'all' ||
                                (taggingFiltersTyped.selectedUntagged === 'true' && !hasTags) ||
                                (taggingFiltersTyped.selectedUntagged === 'false' && hasTags);
        
        // Handle tag filtering - item must have all selected tags
        const matchesTags = taggingFiltersTyped.selectedTags.size === 0 || Array.from(taggingFiltersTyped.selectedTags).every(tagFilter => {
          const [key, value] = tagFilter.split('=');
          return item.tags && item.tags[key] === value;
        });

        // Handle missing-tag filtering - item must not have any of the excluded tags
        const matchesExcludedTags = taggingFiltersTyped.excludedTags.size === 0 || Array.from(taggingFiltersTyped.excludedTags).every(tagFilter => {
          const [key, value] = tagFilter.split('=');
          return !(item.tags && item.tags[key] === value);
        });
        
        if (!matchesSearch || !matchesService || !matchesRegion || !matchesResourceType || !matchesAccount || !matchesFailedToTag || !matchesUntagged || !matchesTags || !matchesExcludedTags) {
          return false;
        }
      } else {
        // Naming convention view - basic filters + naming status
        const namingFiltersTyped = filters as typeof namingFilters;
        const matchesNamingStatus = namingFiltersTyped.selectedNamingStatus === 'all' ||
          (namingFiltersTyped.selectedNamingStatus === 'valid' && isNamingConventionValid(item)) ||
          (namingFiltersTyped.selectedNamingStatus === 'invalid' && isNamingConventionInvalid(item)) ||
          (namingFiltersTyped.selectedNamingStatus === 'not_defined' && (!item.naming_convention_status || item.naming_convention_status === 'not_defined'));
        
        if (!matchesSearch || !matchesService || !matchesRegion || !matchesResourceType || !matchesAccount || !matchesNamingStatus) {
          return false;
        }
      }
      
      // In naming convention mode, filter by acknowledgment status
      if (viewMode === 'naming') {
        const isAcknowledged = isResourceAcknowledged(item);
        const isInvalid = isNamingConventionInvalid(item);
        
        if (acknowledgmentTab === 0) {
          // Not Acknowledged tab: show all not acknowledged (valid and invalid)
          if (isAcknowledged) {
            return false;
          }
        } else {
          // Acknowledged tab: show only acknowledged invalid resources (valid cannot be acknowledged)
          if (!isAcknowledged || !isInvalid) {
            return false;
          }
        }
      }
      
      return true;
    });
  }, [inventory, taggingFilters, namingFilters, viewMode, acknowledgmentTab, acknowledgedArns]);

  // Memoize inventory filtered WITHOUT acknowledgment tab (for counting purposes in naming view)
  const namingFilteredWithoutAckTab = useMemo(() => {
    if (viewMode !== 'naming') return [];
    
    return inventory.filter(item => {
      const filters = namingFilters;
      
      // Enhanced search including tag values
      const searchLower = filters.searchTerm.toLowerCase();
      const matchesSearch = (item.name || '').toLowerCase().includes(searchLower) ||
                           (item.id || '').toLowerCase().includes(searchLower) ||
                           (item.type || '').toLowerCase().includes(searchLower) ||
                           (item.arn || '').toLowerCase().includes(searchLower) ||
                           Object.values(item.tags || {}).some(tagValue => 
                             tagValue.toLowerCase().includes(searchLower)
                           ) ||
                           Object.keys(item.tags || {}).some(tagKey => 
                             tagKey.toLowerCase().includes(searchLower)
                           );
      
      const matchesService = filters.selectedService === 'all' || item.service.toLowerCase() === filters.selectedService.toLowerCase();
      const matchesRegion = filters.selectedRegion === 'all' || item.region === filters.selectedRegion;
      const matchesResourceType = filters.selectedResourceType === 'all' || item.type === filters.selectedResourceType;
      const matchesAccount = filters.selectedAccount === 'all' || item.account === filters.selectedAccount;
      const matchesNamingStatus = filters.selectedNamingStatus === 'all' ||
        (filters.selectedNamingStatus === 'valid' && isNamingConventionValid(item)) ||
        (filters.selectedNamingStatus === 'invalid' && isNamingConventionInvalid(item)) ||
        (filters.selectedNamingStatus === 'not_defined' && (!item.naming_convention_status || item.naming_convention_status === 'not_defined'));
      
      return matchesSearch && matchesService && matchesRegion && matchesResourceType && matchesAccount && matchesNamingStatus;
    });
  }, [inventory, namingFilters, viewMode]);

  // Memoize counts for acknowledgment tabs
  const notAcknowledgedCount = useMemo(() => {
    return namingFilteredWithoutAckTab.filter(item => !isResourceAcknowledged(item)).length;
  }, [namingFilteredWithoutAckTab, acknowledgedArns]);

  const acknowledgedCount = useMemo(() => {
    return namingFilteredWithoutAckTab.filter(item => isResourceAcknowledged(item) && isNamingConventionInvalid(item)).length;
  }, [namingFilteredWithoutAckTab, acknowledgedArns]);

  // Memoize sorted inventory
  const sortedInventory = useMemo(() => {
    return sortInventory(filteredInventory);
  }, [filteredInventory, sortField, sortDirection]);

  // Memoize paginated inventory
  const paginatedInventory = useMemo(() => {
    return sortedInventory.slice(
      (page - 1) * rowsPerPage,
      page * rowsPerPage
    );
  }, [sortedInventory, page, rowsPerPage]);


  const formatTags = (tags: Record<string, string>) => {
    return Object.entries(tags).map(([key, value]) => `${key}=${value}`).join(', ');
  };

  const formatEpochOrUnknown = (dateString: string | undefined | null) => {
    if (dateString == null || String(dateString).trim() === '' || String(dateString).toLowerCase() === 'unknown') {
      return 'Unknown';
    }
    const s = String(dateString);
    let date: Date;
    if (/^\d+$/.test(s)) {
      date = new Date(parseInt(s, 10) * 1000);
    } else {
      date = new Date(s);
    }
    if (Number.isNaN(date.getTime())) {
      return 'Unknown';
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Stable search handlers that don't change on every render
  const handleTaggingSearch = useCallback((value: string) => {
    setTaggingFilters(prev => ({ ...prev, searchTerm: value }));
  }, []);
  
  const handleNamingSearch = useCallback((value: string) => {
    setNamingFilters(prev => ({ ...prev, searchTerm: value }));
  }, []);

  const handleSort = useCallback((field: keyof InventoryItem) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  const handleTagClick = useCallback((tagKey: string, tagValue: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const tagFilter = `${tagKey}=${tagValue}`;
    setTaggingFilters(prev => {
      const newSet = new Set(prev.selectedTags);
      if (newSet.has(tagFilter)) {
        newSet.delete(tagFilter);
      } else {
        newSet.add(tagFilter);
      }
      return { ...prev, selectedTags: newSet };
    });
  }, []);

  // Helper function to get account name from account ID
  const getAccountName = (accountId: string): string => {
    if (!accountId) return '';
    const account = availableAccounts.find(acc => acc.id === accountId);
    return account?.name || accountId;
  };

  // Helper function to get account display text (ID + Name if different)
  const getAccountDisplay = (accountId: string): { id: string; name: string | null } => {
    if (!accountId) return { id: '', name: null };
    const account = availableAccounts.find(acc => acc.id === accountId);
    const name = account?.name;
    return {
      id: accountId,
      name: name && name !== accountId ? name : null
    };
  };

  // Column customization functions
  const handleColumnToggle = (columnKey: string) => {
    setColumnConfig(prev => prev.map(col => 
      col.key === columnKey ? { ...col, visible: !col.visible } : col
    ));
  };

  const handleNamingColumnToggle = (columnKey: string) => {
    setNamingColumnConfig(prev => prev.map(col =>
      col.key === columnKey ? { ...col, visible: !col.visible } : col
    ));
  };

  const handleColumnReset = () => {
    if (viewMode === 'naming') {
      setNamingColumnConfig(DEFAULT_NAMING_COLUMN_CONFIG.map((c) => ({ ...c })));
    } else {
      setColumnConfig([
        { key: 'id', label: 'ID', visible: true, sortable: true, filterable: true },
        { key: 'name', label: 'Name', visible: true, sortable: true, filterable: true },
        { key: 'type', label: 'Type', visible: true, sortable: true, filterable: true },
        { key: 'service', label: 'Service', visible: true, sortable: true, filterable: true },
        { key: 'region', label: 'Region', visible: true, sortable: true, filterable: true },
        { key: 'tags', label: 'Tags', visible: true, sortable: false, filterable: true },
        { key: 'account', label: 'Account', visible: false, sortable: true, filterable: true },
        { key: 'arn', label: 'ARN', visible: false, sortable: true, filterable: true },
        { key: 'creation_time', label: 'Creation Time', visible: false, sortable: true, filterable: true },
        { key: 'discovery_time', label: 'Discovery Time', visible: false, sortable: true, filterable: true },
      ]);
    }
  };


  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshInventory();
    } catch (error) {
      // Error is already handled by the context
      console.error('Failed to refresh inventory:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const exportToCSV = () => {
    // Only export visible columns (use active config for current view mode); exclude 'actions'
    const visibleColumns = activeColumnConfig.filter(col => col.visible && col.key !== 'actions');
    const headers = visibleColumns.map(col => col.label);
    const csvContent = [
      headers.join(','),
      ...filteredInventory.map(item => 
        visibleColumns.map(col => {
          let value = '';
          if (col.key === 'tags') {
            value = formatTags(item.tags);
          } else if (col.key === 'creation_time' || col.key === 'discovery_time') {
            value = formatEpochOrUnknown(item[col.key as keyof InventoryItem] as string);
          } else {
            value = item[col.key as keyof InventoryItem] as string || '';
          }
          return `"${value}"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inventory-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle item selection
  const handleSelectItem = useCallback((arn: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(arn)) {
        newSet.delete(arn);
      } else {
        newSet.add(arn);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = () => {
    if (selectedItems.size === paginatedInventory.length) {
      setSelectedItems(new Set());
    } else {
      const allArns = new Set(paginatedInventory.map(item => item.arn));
      setSelectedItems(allArns);
    }
  };

  const handleOpenManualTagDialog = () => {
    if (selectedItems.size === 0) {
      showNotification({
        type: 'warning',
        title: 'No Resources Selected',
        message: 'Please select at least one resource to tag',
      });
      return;
    }
    setManualTagDialogOpen(true);
  };

  // Handle acknowledgment
  const handleAcknowledge = async (arn: string) => {
    try {
      await tagopsService.acknowledgeNamingConvention(arn);
      setAcknowledgedArns(prev => new Set(prev).add(arn));
      showNotification({
        type: 'success',
        title: 'Acknowledged',
        message: 'Resource naming convention has been acknowledged',
      });
      // Refresh inventory to get updated acknowledgment status
      await refreshInventory();
    } catch (error: any) {
      showNotification(createErrorNotification(error, undefined, 'Failed to acknowledge resource'));
    }
  };

  // Handle delete acknowledgment
  const handleDeleteAcknowledgment = async (arn: string) => {
    try {
      await tagopsService.deleteNamingConventionAcknowledgment(arn);
      setAcknowledgedArns(prev => {
        const newSet = new Set(prev);
        newSet.delete(arn);
        return newSet;
      });
      showNotification({
        type: 'success',
        title: 'Acknowledgment Removed',
        message: 'Resource acknowledgment has been removed',
      });
      // Refresh inventory to get updated acknowledgment status
      await refreshInventory();
    } catch (error: any) {
      showNotification(createErrorNotification(error, undefined, 'Failed to remove acknowledgment'));
    }
  };

  // Handle edit name
  const handleOpenEditNameDialog = (item: InventoryItem) => {
    setEditingResource(item);
    setEditingName(item.name || '');
    setEditNameDialogOpen(true);
  };

  const handleCloseEditNameDialog = () => {
    setEditNameDialogOpen(false);
    setEditingResource(null);
    setEditingName('');
  };

  const handleSaveName = async () => {
    if (!editingResource) return;
    const trimmed = editingName.trim();
    const validationError = validateResourceName(editingName);
    if (validationError) return;

    setIsSavingName(true);
    try {
      await tagopsService.addTagsManually(
        [editingResource.arn],
        { Name: trimmed },
        { [editingResource.arn]: editingResource.region }
      );
      showNotification({
        type: 'success',
        title: 'Name Updated',
        message: 'Resource name has been updated successfully',
      });
      handleCloseEditNameDialog();
      await refreshInventory();
    } catch (error: any) {
      showNotification({
        type: 'error',
        title: 'Failed to Update Name',
        message: error?.response?.data?.error || error?.message || 'Failed to update resource name',
      });
    } finally {
      setIsSavingName(false);
    }
  };

  // Get column config based on view mode
  const getColumnConfigForMode = (): ColumnConfig[] => {
    if (viewMode === 'naming') return namingColumnConfig;
    return columnConfig;
  };

  const activeColumnConfig = getColumnConfigForMode();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Resource Inventory
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {viewMode === 'tagging' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenManualTagDialog}
              disabled={loading || selectedItems.size === 0}
              sx={{ 
                color: 'black',
                '&.Mui-disabled': {
                  color: 'black',
                  opacity: 0.5,
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  '& .MuiSvgIcon-root': {
                    color: 'black',
                    opacity: 0.5,
                  }
                }
              }}
            >
              Modify Tags Manually
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={exportToCSV}
            disabled={loading || filteredInventory.length === 0}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
      </Box>


      {/* Service Statistics */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        {serviceStats.map((stat) => (
          <Box
            key={stat.service}
            sx={{
              px: 2,
              py: 1,
              borderRadius: 1,
              bgcolor: 'grey.100',
              border: '1px solid',
              borderColor: 'grey.300',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 'fit-content',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'grey.200',
              }
            }}
            onClick={() => {
              const currentService = viewMode === 'tagging' ? taggingFilters.selectedService : namingFilters.selectedService;
              const newService = currentService === stat.service ? 'all' : stat.service;
              if (viewMode === 'tagging') {
                setTaggingFilters(prev => ({ ...prev, selectedService: newService }));
              } else {
                setNamingFilters(prev => ({ ...prev, selectedService: newService }));
              }
            }}
          >
            <Box
              component="img"
              src={getServiceIcon(stat.service)}
              sx={{ 
                width: 20, 
                height: 20,
                borderRadius: 1,
                objectFit: 'contain'
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {stat.count}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatServiceName(stat.service)}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              pb: filtersExpanded ? 1 : 2,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            <Typography variant="h6">
              {viewMode === 'tagging' ? 'Tag Management Filters' : 'Naming Compliance Filters'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={(e) => {
                e.stopPropagation();
                setFiltersExpanded(!filtersExpanded);
              }}>
                {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>
          
          <Collapse in={filtersExpanded}>
            <Box sx={{ p: 2, pt: 0 }}>
              <Grid container spacing={2}>
                {/* Common filters for both views */}
                <Grid item xs={12} md={3} sx={{ flexBasis: { md: '25%' }, flexGrow: 0, flexShrink: 0 }}>
                  {viewMode === 'tagging' ? (
                    <SearchInput
                      value={taggingFilters.searchTerm}
                      onSearch={handleTaggingSearch}
                    />
                  ) : (
                    <SearchInput
                      value={namingFilters.searchTerm}
                      onSearch={handleNamingSearch}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={2}>
                  <Autocomplete
                    options={['all', ...availableServices]}
                    value={viewMode === 'tagging' ? taggingFilters.selectedService : namingFilters.selectedService}
                    onChange={(_, newValue) => {
                      if (viewMode === 'tagging') {
                        setTaggingFilters(prev => ({ ...prev, selectedService: newValue || 'all' }));
                      } else {
                        setNamingFilters(prev => ({ ...prev, selectedService: newValue || 'all' }));
                      }
                    }}
                    getOptionLabel={(option) => option === 'all' ? 'All Services' : formatServiceName(option)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Service"
                        placeholder="Search services..."
                      />
                    )}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Autocomplete
                    options={['all', ...availableRegions]}
                    value={viewMode === 'tagging' ? taggingFilters.selectedRegion : namingFilters.selectedRegion}
                    onChange={(_, newValue) => {
                      if (viewMode === 'tagging') {
                        setTaggingFilters(prev => ({ ...prev, selectedRegion: newValue || 'all' }));
                      } else {
                        setNamingFilters(prev => ({ ...prev, selectedRegion: newValue || 'all' }));
                      }
                    }}
                    getOptionLabel={(option) => option === 'all' ? 'All Regions' : option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Region"
                        placeholder="Search regions..."
                      />
                    )}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Autocomplete
                    options={['all', ...availableResourceTypes]}
                    value={viewMode === 'tagging' ? taggingFilters.selectedResourceType : namingFilters.selectedResourceType}
                    onChange={(_, newValue) => {
                      if (viewMode === 'tagging') {
                        setTaggingFilters(prev => ({ ...prev, selectedResourceType: newValue || 'all' }));
                      } else {
                        setNamingFilters(prev => ({ ...prev, selectedResourceType: newValue || 'all' }));
                      }
                    }}
                    getOptionLabel={(option) => {
                      if (option === 'all') {
                        const selectedService = viewMode === 'tagging' ? taggingFilters.selectedService : namingFilters.selectedService;
                        return selectedService !== 'all' ? `All ${formatServiceName(selectedService)} Types` : 'All Types';
                      }
                      return option;
                    }}
                    renderInput={(params) => {
                      const selectedService = viewMode === 'tagging' ? taggingFilters.selectedService : namingFilters.selectedService;
                      return (
                        <TextField
                          {...params}
                          label={selectedService !== 'all' ? `${formatServiceName(selectedService)} Types` : 'Resource Type'}
                          placeholder="Search types..."
                        />
                      );
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Autocomplete
                    options={[{ id: 'all', name: 'All Accounts' }, ...availableAccounts]}
                    value={availableAccounts.find(acc => acc.id === (viewMode === 'tagging' ? taggingFilters.selectedAccount : namingFilters.selectedAccount)) || { id: 'all', name: 'All Accounts' }}
                    onChange={(_, newValue) => {
                      const accountId = newValue?.id || 'all';
                      if (viewMode === 'tagging') {
                        setTaggingFilters(prev => ({ ...prev, selectedAccount: accountId }));
                      } else {
                        setNamingFilters(prev => ({ ...prev, selectedAccount: accountId }));
                      }
                    }}
                    getOptionLabel={(option) => {
                      if (option.id === 'all') return option.name || 'All Accounts';
                      const name = option.name || '';
                      return name && name !== option.id ? `${option.id} - ${name}` : option.id;
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Account"
                        placeholder="Search accounts..."
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        {option.id === 'all' ? (
                          <Typography variant="body2">{option.name || 'All Accounts'}</Typography>
                        ) : (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {option.id}
                            </Typography>
                            {option.name && option.name !== option.id && (
                              <Typography variant="caption" color="text.secondary">
                                {option.name}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                    fullWidth
                  />
                </Grid>
              </Grid>
              
              {/* Tagging-specific filters */}
              {viewMode === 'tagging' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={3}>
                    <Autocomplete
                      options={availableTags.filter(tag => {
                        const tagFilter = `${tag.key}=${tag.value}`;
                        return !taggingFilters.selectedTags.has(tagFilter) && !taggingFilters.excludedTags.has(tagFilter);
                      })}
                      value={null}
                      onChange={(_, newValue) => {
                        if (newValue) {
                          const tagFilter = `${newValue.key}=${newValue.value}`;
                          setTaggingFilters(prev => ({
                            ...prev,
                            selectedTags: new Set(prev.selectedTags).add(tagFilter)
                          }));
                        }
                      }}
                      getOptionLabel={(option) => option.display}
                      isOptionEqualToValue={(option, value) => 
                        option.key === value.key && option.value === value.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Filter by Tag"
                          placeholder="Search tags..."
                          helperText="Show resources that have this tag"
                        />
                      )}
                      fullWidth
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {option.key}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            : {option.value}
                          </Typography>
                        </Box>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Autocomplete
                      options={availableTags.filter(tag => {
                        const tagFilter = `${tag.key}=${tag.value}`;
                        return !taggingFilters.excludedTags.has(tagFilter) && !taggingFilters.selectedTags.has(tagFilter);
                      })}
                      value={null}
                      onChange={(_, newValue) => {
                        if (newValue) {
                          const tagFilter = `${newValue.key}=${newValue.value}`;
                          setTaggingFilters(prev => ({
                            ...prev,
                            excludedTags: new Set(prev.excludedTags).add(tagFilter)
                          }));
                        }
                      }}
                      getOptionLabel={(option) => option.display}
                      isOptionEqualToValue={(option, value) =>
                        option.key === value.key && option.value === value.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Without Tag"
                          placeholder="Search tags..."
                          helperText="Show resources that do not have this tag"
                        />
                      )}
                      fullWidth
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {option.key}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            : {option.value}
                          </Typography>
                        </Box>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Tagged</InputLabel>
                      <Select
                        value={taggingFilters.selectedUntagged}
                        label="Tagged"
                        onChange={(e) => setTaggingFilters(prev => ({ ...prev, selectedUntagged: e.target.value }))}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="false">Tagged</MenuItem>
                        <MenuItem value="true">Untagged</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Failed to Tag</InputLabel>
                      <Select
                        value={taggingFilters.selectedFailedToTag}
                        label="Failed to Tag"
                        onChange={(e) => setTaggingFilters(prev => ({ ...prev, selectedFailedToTag: e.target.value }))}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="true">True</MenuItem>
                        <MenuItem value="false">False</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
              
              {/* Naming convention specific filters */}
              {viewMode === 'naming' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Naming Status</InputLabel>
                      <Select
                        value={namingFilters.selectedNamingStatus}
                        label="Naming Status"
                        onChange={(e) => setNamingFilters(prev => ({ ...prev, selectedNamingStatus: e.target.value }))}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="valid">Valid</MenuItem>
                        <MenuItem value="invalid">Invalid</MenuItem>
                        <MenuItem value="not_defined">Not Defined</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
              
              {/* Clear Filters Button */}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => {
                    if (viewMode === 'tagging') {
                      setTaggingFilters({
                        searchTerm: '',
                        selectedService: 'all',
                        selectedRegion: 'all',
                        selectedResourceType: 'all',
                        selectedAccount: 'all',
                        selectedFailedToTag: 'all',
                        selectedUntagged: 'all',
                        selectedTags: new Set(),
                        excludedTags: new Set(),
                      });
                    } else {
                      setNamingFilters({
                        searchTerm: '',
                        selectedService: 'all',
                        selectedRegion: 'all',
                        selectedResourceType: 'all',
                        selectedAccount: 'all',
                        selectedNamingStatus: 'all',
                      });
                    }
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
      
      {/* View Mode and Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={rowsPerPage}
                  label="Items per page"
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1); // Reset to first page when changing page size
                  }}
                >
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(_, newMode) => {
                    if (newMode !== null) {
                      setViewMode(newMode);
                      setPage(1); // Reset to first page when switching modes
                    }
                  }}
                  aria-label="view mode"
                  size="small"
                  sx={{ width: '100%' }}
                >
                  <ToggleButton value="tagging" aria-label="tag management" sx={{ flex: 1 }}>
                    Tag Management
                  </ToggleButton>
                  <ToggleButton value="naming" aria-label="naming compliance" sx={{ flex: 1 }}>
                    Naming Compliance
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: viewMode === 'naming' ? 'block' : 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <ToggleButtonGroup
                  value={acknowledgmentTab}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) {
                      setAcknowledgmentTab(newValue);
                      setPage(1); // Reset to first page when switching tabs
                    }
                  }}
                  aria-label="acknowledgment status"
                  size="small"
                  sx={{ width: '100%' }}
                >
                  <ToggleButton value={0} aria-label="not acknowledged" sx={{ flex: 1 }}>
                    Not Acknowledged ({notAcknowledgedCount})
                  </ToggleButton>
                  <ToggleButton value={1} aria-label="acknowledged" sx={{ flex: 1 }}>
                    Acknowledged ({acknowledgedCount})
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Active Tag Filters */}
      {viewMode === 'tagging' && (taggingFilters.selectedTags.size > 0 || taggingFilters.excludedTags.size > 0) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Active Tag Filters:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Array.from(taggingFilters.selectedTags).map((tagFilter) => {
              const [key, value] = tagFilter.split('=');
              return (
                <Chip
                  key={tagFilter}
                  label={`${key}: ${value}`}
                  color="primary"
                  variant="filled"
                  onDelete={() => {
                    setTaggingFilters(prev => {
                      const newSet = new Set(prev.selectedTags);
                      newSet.delete(tagFilter);
                      return { ...prev, selectedTags: newSet };
                    });
                  }}
                  sx={{
                    '& .MuiChip-deleteIcon': {
                      color: 'primary.contrastText',
                    },
                  }}
                />
              );
            })}
            {Array.from(taggingFilters.excludedTags).map((tagFilter) => {
              const [key, value] = tagFilter.split('=');
              return (
                <Chip
                  key={`excluded-${tagFilter}`}
                  label={`Without ${key}: ${value}`}
                  color="warning"
                  variant="filled"
                  onDelete={() => {
                    setTaggingFilters(prev => {
                      const newSet = new Set(prev.excludedTags);
                      newSet.delete(tagFilter);
                      return { ...prev, excludedTags: newSet };
                    });
                  }}
                  sx={{
                    '& .MuiChip-deleteIcon': {
                      color: 'warning.contrastText',
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}

      {/* Results Summary */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {paginatedInventory.length} of {filteredInventory.length} resources
          </Typography>
          {selectedItems.size > 0 && (
            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
              {selectedItems.size} selected
            </Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ViewColumnIcon />}
          onClick={() => setColumnDialogOpen(true)}
        >
          Columns
        </Button>
      </Box>

      {/* Inventory Table */}
      <TableContainer component={Paper}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            {viewMode === 'tagging' && <col style={{ width: '48px' }} />}
            {activeColumnConfig.filter(col => col.visible).map((col) => (
              <col key={col.key} style={{ width: getInventoryColumnWidth(col.key) }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {viewMode === 'tagging' && (
                <TableCell padding="none" sx={{ width: 48, minWidth: 48, maxWidth: 48, padding: '0px !important', paddingLeft: '8px !important', paddingRight: '0px !important' }}>
                  <Checkbox
                    indeterminate={selectedItems.size > 0 && selectedItems.size < paginatedInventory.length}
                    checked={paginatedInventory.length > 0 && selectedItems.size === paginatedInventory.length}
                    onChange={handleSelectAll}
                    size="small"
                    sx={{ padding: '0px', margin: '0px' }}
                  />
                </TableCell>
              )}
              {activeColumnConfig.filter(col => col.visible).map((column) => (
                <TableCell
                  key={column.key}
                  sx={{
                    width: getInventoryColumnWidth(column.key),
                    maxWidth: getInventoryColumnWidth(column.key),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {column.sortable && column.key !== 'actions' ? (
                    <TableSortLabel
                      active={sortField === column.key}
                      direction={sortField === column.key ? sortDirection : 'asc'}
                      onClick={() => {
                        if (column.key === 'tags') {
                          // Handle tags sorting differently since it's not a direct property
                          return;
                        }
                        if (column.key === 'naming_convention_status') {
                          if (sortField === column.key) {
                            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                          } else {
                            setSortField(column.key as keyof InventoryItem);
                            setSortDirection('asc');
                          }
                          return;
                        }
                        handleSort(column.key as keyof InventoryItem);
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={activeColumnConfig.filter(col => col.visible).length + (viewMode === 'tagging' ? 1 : 0)} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : paginatedInventory.length === 0 ? (
              <TableRow>
                <TableCell colSpan={activeColumnConfig.filter(col => col.visible).length + (viewMode === 'tagging' ? 1 : 0)} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No resources found matching your criteria
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedInventory.map((item, index) => {
                // Check if failed_to_tag is true (default to false if not present)
                const failedToTag = item.failed_to_tag ?? false;
                const isSelected = selectedItems.has(item.arn);
                const isAcknowledged = isResourceAcknowledged(item);
                const isInvalid = isNamingConventionInvalid(item);
                const isNameEditable = item.name_editteble === true;
                return (
                <TableRow 
                  key={`${item.id}-${item.service}-${item.region}-${index}`} 
                  hover
                  selected={isSelected}
                  sx={{
                    borderLeft: failedToTag ? '4px solid' : 'none',
                    borderLeftColor: failedToTag ? 'warning.main' : 'transparent',
                    '&:hover': {
                      borderLeftColor: failedToTag ? 'warning.dark' : 'transparent',
                    }
                  }}
                >
                  {viewMode === 'tagging' && (
                    <TableCell padding="none" sx={{ width: 48, minWidth: 48, maxWidth: 48, padding: '0px !important', paddingLeft: '8px !important', paddingRight: '0px !important' }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelectItem(item.arn)}
                        size="small"
                        sx={{ padding: '0px', margin: '0px' }}
                      />
                    </TableCell>
                  )}
                  {activeColumnConfig.filter(col => col.visible).map((column) => (
                    <TableCell
                      key={column.key}
                      sx={{
                        width: getInventoryColumnWidth(column.key),
                        maxWidth: getInventoryColumnWidth(column.key),
                        overflow: 'hidden',
                        textOverflow: column.key === 'arn' || column.key === 'name' || column.key === 'id' ? 'clip' : 'ellipsis',
                        whiteSpace: (column.key === 'tags' || column.key === 'arn' || column.key === 'name' || column.key === 'id') ? 'normal' : 'nowrap',
                        verticalAlign: 'top',
                      }}
                    >
                      {column.key === 'arn' && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          {failedToTag && (
                            <Tooltip title="This resource failed to be tagged automatically" arrow>
                              <WarningIcon 
                                sx={{ 
                                  color: 'warning.main', 
                                  fontSize: '1rem',
                                  flexShrink: 0,
                                  cursor: 'help',
                                  mt: 0.25
                                }} 
                              />
                            </Tooltip>
                          )}
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all', whiteSpace: 'normal' }}>
                            {item.arn}
                          </Typography>
                        </Box>
                      )}
                      {column.key === 'id' && (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all', whiteSpace: 'normal' }}>
                          {item.id}
                        </Typography>
                      )}
                      {column.key === 'name' && (
                        <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                          {item.name}
                        </Typography>
                      )}
                      {column.key === 'type' && (
                        <Chip
                          label={item.type}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {column.key === 'service' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            component="img"
                            src={getServiceIcon(item.service)}
                            sx={{ 
                              width: 24, 
                              height: 24,
                              borderRadius: 1,
                              objectFit: 'contain'
                            }}
                          />
                          <Typography variant="body2">
                            {formatServiceName(item.service)}
                          </Typography>
                        </Box>
                      )}
                      {column.key === 'region' && (
                        <Typography variant="body2">
                          {item.region}
                        </Typography>
                      )}
                      {column.key === 'tags' && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '100%' }}>
                          {Object.entries(item.tags || {}).map(([key, value]) => {
                            const tagFilter = `${key}=${value}`;
                            const selectedTagsSet = viewMode === 'tagging' ? taggingFilters.selectedTags : new Set<string>();
                            const isSelected = selectedTagsSet.has(tagFilter);
                            return (
                              <Chip
                                key={`${key}-${value}`}
                                label={`${key}: ${value}`}
                                size="small"
                                variant={isSelected ? "filled" : "outlined"}
                                color={isSelected ? "primary" : "default"}
                                onClick={(e) => handleTagClick(key, value, e)}
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: isSelected ? 'primary.dark' : 'action.hover',
                                  },
                                  maxWidth: '100%',
                                }}
                                title={`Click to ${isSelected ? 'remove' : 'add'} filter: ${key}=${value}`}
                              />
                            );
                          })}
                        </Box>
                      )}
                      {column.key === 'account' && (
                        <Box>
                          {(() => {
                            const accountDisplay = getAccountDisplay(item.account);
                            return (
                              <>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                  {accountDisplay.id}
                                </Typography>
                                {accountDisplay.name && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {accountDisplay.name}
                                  </Typography>
                                )}
                              </>
                            );
                          })()}
                        </Box>
                      )}
                      {column.key === 'creation_time' && (
                        <Typography variant="body2">
                          {formatEpochOrUnknown(item.creation_time)}
                        </Typography>
                      )}
                      {column.key === 'discovery_time' && (
                        <Typography variant="body2">
                          {formatEpochOrUnknown(item.discovery_time)}
                        </Typography>
                      )}
                      {column.key === 'naming_convention_status' && (
                        <Box>
                          {isInvalid && (
                            <Chip
                              label={isAcknowledged ? 'Acknowledged' : 'Invalid'}
                              size="small"
                              color={isAcknowledged ? 'default' : 'error'}
                              variant={isAcknowledged ? 'outlined' : 'filled'}
                            />
                          )}
                          {isNamingConventionValid(item) && (
                            <Chip
                              label="Valid"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {(!item.naming_convention_status || item.naming_convention_status === 'not_defined') && (
                            <Chip
                              label="Not Defined"
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      )}
                      {column.key === 'actions' && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Tooltip title={isNameEditable ? "Edit Name" : "Name is not editable"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditNameDialog(item)}
                                color="primary"
                                disabled={!isNameEditable}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={isInvalid ? (isAcknowledged ? "Remove Acknowledgment" : "Acknowledge") : "Acknowledgment only available for invalid resources"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (isAcknowledged) {
                                    handleDeleteAcknowledgment(item.arn);
                                  } else {
                                    handleAcknowledge(item.arn);
                                  }
                                }}
                                color={isInvalid && isAcknowledged ? "error" : "primary"}
                                disabled={!isInvalid}
                              >
                                {isInvalid && isAcknowledged ? (
                                  <CancelIcon fontSize="small" />
                                ) : (
                                  <CheckCircleIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {sortedInventory.length > rowsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(sortedInventory.length / rowsPerPage)}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      )}

      {/* Column Customization Dialog */}
      <Dialog open={columnDialogOpen} onClose={() => setColumnDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Customize Columns</Typography>
            <Button onClick={handleColumnReset} size="small">
              Reset to Default
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which columns to display in the inventory table:
          </Typography>
          <FormGroup>
            {activeColumnConfig.map((column) => (
              <FormControlLabel
                key={column.key}
                control={
                  <Checkbox
                    checked={column.visible}
                    onChange={() => {
                      if (viewMode === 'naming') {
                        handleNamingColumnToggle(column.key);
                      } else {
                        handleColumnToggle(column.key);
                      }
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{column.label}</Typography>
                    {!column.sortable && (
                      <Chip label="Not sortable" size="small" variant="outlined" />
                    )}
                  </Box>
                }
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Tag Editor Dialog */}
      <ManualTagEditorDialog
        open={manualTagDialogOpen}
        onClose={() => setManualTagDialogOpen(false)}
        selectedItems={Array.from(selectedItems).map(arn => 
          inventory.find(item => item.arn === arn)
        ).filter(Boolean) as InventoryItem[]}
        managedTagKeys={managedTagKeys}
        onSuccess={() => {
          setManualTagDialogOpen(false);
          setSelectedItems(new Set());
          refreshInventory();
        }}
      />

      {/* Edit Name Dialog */}
      <Dialog open={editNameDialogOpen} onClose={handleCloseEditNameDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Resource Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Resource Name"
            fullWidth
            variant="outlined"
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            disabled={isSavingName}
            error={!!validateResourceName(editingName)}
            helperText={validateResourceName(editingName)}
            sx={{ mt: 2 }}
          />
          {editingResource && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              ARN: {editingResource.arn}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditNameDialog} disabled={isSavingName}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveName}
            variant="contained"
            disabled={isSavingName || !editingName.trim() || !!validateResourceName(editingName)}
          >
            {isSavingName ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
